// The boundary between stored documents and the core's records: reading a document's raw values with the rules of
// Swift's synthesized Codable (as Firestore's decoder applies them in the kit), and writing them back.
//
// The core imports nothing from Firebase. A document arrives as plain data; a moment in it is anything with
// `toDate()` (Firestore's Timestamp) or `{seconds, nanoseconds}`. Records keep moments as Dates; when they are written,
// the data layer passes `toStoredMoment` (Timestamp.fromDate) to turn them back.

import { dayOfStored } from "../days";
import type { Day } from "../types";
import { normalizedUUID } from "../ids";

/** A stored document's data, or a part of it: what Firestore hands over, what the core gives back to write. */
export type Fields = Record<string, unknown>;

/** What reading needs: the zone old days are read in (the browser's, `currentZone()`). */
export interface DecodeContext {
  zone: string;
}

/** What writing needs: how a Date becomes a stored moment (Timestamp.fromDate). Without it, moments stay Dates. */
export interface EncodeOptions {
  toStoredMoment?: (date: Date) => unknown;
}

/** A decoded record, or why it couldn't be read. A record that can't be read is skipped and reported, never rewritten. */
export type Decoded<T> = { ok: true; value: T } | { ok: false; error: string };

/** The fields the back end puts on every document, which belong to no record (FirestoreBackend.swift). */
export const BACKEND_FIELDS: readonly string[] = ["modified", "deleted"];

/** Thrown inside a decoder only; the decoders turn it into a `Decoded` failure, so none of them throws. */
export class DecodeFailure extends Error {}

/** Runs a decoder, turning any failure into a result. */
export function decoding<T>(read: () => T): Decoded<T> {
  try {
    return { ok: true, value: read() };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : String(error) };
  }
}

/** Whether a document is a tombstone: a record deleted elsewhere (`{deleted: true, modified}`). */
export function isTombstone(fields: Fields): boolean {
  return fields.deleted === true;
}

// MARK: - Raw values

export function isPlainObject(value: unknown): value is Fields {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const prototype: unknown = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

interface TimestampLike {
  toDate(): Date;
}

function hasToDate(value: unknown): value is TimestampLike {
  return typeof value === "object" && value !== null && typeof (value as { toDate?: unknown }).toDate === "function";
}

function secondsAndNanoseconds(value: unknown): { seconds: number; nanoseconds: number } | null {
  if (typeof value !== "object" || value === null) return null;
  const { seconds, nanoseconds } = value as { seconds?: unknown; nanoseconds?: unknown };
  return typeof seconds === "number" && typeof nanoseconds === "number" ? { seconds, nanoseconds } : null;
}

/** A stored moment as a Date, or null when the value isn't one. */
export function momentOf(value: unknown): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (hasToDate(value)) {
    const date = value.toDate();
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
  }
  const parts = secondsAndNanoseconds(value);
  return parts ? new Date(parts.seconds * 1000 + Math.floor(parts.nanoseconds / 1_000_000)) : null;
}

/** Whether a stored moment holds nothing finer than a millisecond (a Timestamp may hold nanoseconds). */
function isWholeMilliseconds(value: unknown): boolean {
  const parts = secondsAndNanoseconds(value);
  return parts ? parts.nanoseconds % 1_000_000 === 0 : true;
}

// MARK: - Reading values (each throws `DecodeFailure` for a value Swift's decoder would refuse)

export type ValueReader<T> = (value: unknown, path: string, context: DecodeContext) => T;

function wrongShape(path: string): never {
  throw new DecodeFailure(`${path} has the wrong shape`);
}

export const readString: ValueReader<string> = (value, path) => (typeof value === "string" ? value : wrongShape(path));

export const readBool: ValueReader<boolean> = (value, path) => (typeof value === "boolean" ? value : wrongShape(path));

/** A Double: any finite number. */
export const readNumber: ValueReader<number> = (value, path) =>
  typeof value === "number" && Number.isFinite(value) ? value : wrongShape(path);

/** An Int: a whole number. */
export const readInt: ValueReader<number> = (value, path) => (Number.isInteger(value) ? (value as number) : wrongShape(path));

/** A UUID in any case, kept upper case as Swift writes it back. */
export const readUUID: ValueReader<string> = (value, path) => normalizedUUID(value) ?? wrongShape(path);

export const readMoment: ValueReader<Date> = (value, path) => momentOf(value) ?? wrongShape(path);

/** A day (`StoredAsDay`): a stored moment read tolerantly in the context's zone (days.ts). */
export const readDay: ValueReader<Day> = (value, path, context) => {
  const moment = momentOf(value) ?? wrongShape(path);
  return dayOfStored(moment, context.zone, isWholeMilliseconds(value));
};

export function readArray<T>(element: ValueReader<T>): ValueReader<T[]> {
  return (value, path, context) => {
    if (!Array.isArray(value)) wrongShape(path);
    // Swift's decoder refuses a null in an array of non-optionals, as it refuses any other wrong element.
    return value.map((item, index) => element(item, `${path}[${index}]`, context));
  };
}

/** A map with string keys (`[String: T]`). */
export function readMap<T>(element: ValueReader<T>): ValueReader<Record<string, T>> {
  return (value, path, context) => {
    if (!isPlainObject(value)) wrongShape(path);
    const result: Record<string, T> = {};
    for (const [key, item] of Object.entries(value)) result[key] = element(item, `${path}.${key}`, context);
    return result;
  };
}

/** A nested struct, read with its own reader. */
export function readStruct<T>(read: (fields: FieldReader) => T): ValueReader<T> {
  return (value, path, context) => {
    if (!isPlainObject(value)) wrongShape(path);
    return read(new FieldReader(value, context, `${path}.`));
  };
}

export function readEnum<T extends string>(cases: readonly T[]): ValueReader<T> {
  return (value, path) => ((cases as readonly unknown[]).includes(value) ? (value as T) : wrongShape(path));
}

export const readStrings = readArray(readString);

/**
 * A case of an enum as Swift's synthesized Codable stores it: a map whose only key (of the cases known) is the case,
 * holding `{}` or `{_0: value}`. Keys that are no case are not counted, as Swift's decoder doesn't see them. Null
 * when the value is no such map.
 */
export function enumCase<T extends string>(value: unknown, cases: readonly T[]): { name: T; payload: unknown } | null {
  if (!isPlainObject(value)) return null;
  const known = Object.keys(value).filter((key): key is T => (cases as readonly string[]).includes(key));
  if (known.length !== 1) return null;
  const name = known[0] as T;
  return { name, payload: value[name] };
}

/** The associated value of a case (`{_0: value}`), read with `read`; throws when it isn't there or doesn't fit. */
export function associatedValue<T>(payload: unknown, read: ValueReader<T>, path: string, context: DecodeContext): T {
  if (!isPlainObject(payload)) wrongShape(path);
  const value = payload._0;
  if (value === undefined || value === null) wrongShape(path);
  return read(value, `${path}._0`, context);
}

/** Reads the fields of one stored map, with the rules of Swift's synthesized Codable. */
export class FieldReader {
  constructor(readonly fields: Fields, readonly context: DecodeContext, private readonly path = "") {}

  /** A field's value; a stored null counts as missing, as `decodeIfPresent` reads it. */
  value(key: string): unknown {
    const value = this.fields[key];
    return value === null ? undefined : value;
  }

  /** A field every record has: missing, the record can't be read. */
  required<T>(key: string, read: ValueReader<T>): T {
    const value = this.value(key);
    if (value === undefined) throw new DecodeFailure(`${this.path}${key} is missing`);
    return read(value, `${this.path}${key}`, this.context);
  }

  /** An optional field: missing is undefined, but a value of the wrong kind still makes the record unreadable. */
  optional<T>(key: string, read: ValueReader<T>): T | undefined {
    const value = this.value(key);
    return value === undefined ? undefined : read(value, `${this.path}${key}`, this.context);
  }

  string(key: string): string { return this.required(key, readString); }
  optionalString(key: string): string | undefined { return this.optional(key, readString); }
  bool(key: string): boolean { return this.required(key, readBool); }
  optionalBool(key: string): boolean | undefined { return this.optional(key, readBool); }
  number(key: string): number { return this.required(key, readNumber); }
  optionalNumber(key: string): number | undefined { return this.optional(key, readNumber); }
  int(key: string): number { return this.required(key, readInt); }
  optionalInt(key: string): number | undefined { return this.optional(key, readInt); }
  uuid(key: string): string { return this.required(key, readUUID); }
  optionalUUID(key: string): string | undefined { return this.optional(key, readUUID); }
  moment(key: string): Date { return this.required(key, readMoment); }
  optionalMoment(key: string): Date | undefined { return this.optional(key, readMoment); }
  optionalDay(key: string): Day | undefined { return this.optional(key, readDay); }
  strings(key: string): string[] { return this.required(key, readStrings); }
  optionalStrings(key: string): string[] | undefined { return this.optional(key, readStrings); }

  /** The top-level fields this version doesn't know (nor the back end's), kept to be written back unchanged. */
  unknownFields(known: readonly string[]): Fields {
    const result: Fields = {};
    for (const [key, value] of Object.entries(this.fields)) {
      if (!known.includes(key) && !BACKEND_FIELDS.includes(key)) result[key] = value;
    }
    return result;
  }
}

// MARK: - Writing values

/**
 * Builds a document as Swift's encoder writes it: an absent optional (undefined) is left out, never written as
 * null. Unknown fields come first, so a known field always wins.
 */
export function document(unknownFields: Fields, entries: Fields): Fields {
  const result: Fields = { ...unknownFields };
  for (const [key, value] of Object.entries(entries)) {
    if (value !== undefined) result[key] = value;
  }
  return result;
}

/** A document ready for Firestore: every Date in its known values turned into a stored moment. */
export function storedFields(fields: Fields, options?: EncodeOptions): Fields {
  const convert = options?.toStoredMoment;
  if (!convert) return fields;
  const map = (value: unknown): unknown => {
    if (value instanceof Date) return convert(value);
    if (Array.isArray(value)) return value.map(map);
    // Only plain maps: a Timestamp or a sentinel kept from the stored document is written back as it came.
    if (isPlainObject(value)) return Object.fromEntries(Object.entries(value).map(([key, inner]) => [key, map(inner)]));
    return value;
  };
  return map(fields) as Fields;
}

/**
 * Whether two stored values are the same, as the kit's `FieldChanges` compares them (Foundation's `isEqual`): Dates
 * by their moment, arrays in order, maps by their keys, anything with `isEqual` (a Timestamp) by that.
 */
export function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((item, index) => sameValue(item, b[index]));
  if (isPlainObject(a) && isPlainObject(b)) {
    const keys = Object.keys(a);
    return keys.length === Object.keys(b).length && keys.every((key) => key in b && sameValue(a[key], b[key]));
  }
  if (typeof a === "object" && a !== null && typeof (a as { isEqual?: unknown }).isEqual === "function") {
    return (a as { isEqual(other: unknown): boolean }).isEqual(b);
  }
  return false;
}

/** The record without keys whose value is undefined, so that an absent field is absent, not present as undefined. */
export function withoutUndefined<T extends object>(record: T): T {
  for (const key of Object.keys(record) as (keyof T)[]) {
    if (record[key] === undefined) delete record[key];
  }
  return record;
}
